import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NonAwardReason,
  SourcingEventStatus,
  useCancelSourcingEventMutation,
  useCloseSourcingEventMutation,
  useGetCancellationScopeQuery,
  type SourcingEventDto,
} from "../../autogen/bff-api";
import { isApiErrorDto } from "../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../common/errors/useApiError";
import { visibleLength } from "../../common/text/visibleLength";
import { nonAwardReasons } from "./result-notice/nonAwardReasons";
import { announcedOnDoffin } from "./announcedOnDoffin";

export type ReasonAction = "cancel" | "close";

type CancelMode = "plain" | "beforeDeadline" | "afterDeadline" | "withdrawAward";

// FOA § 25-4 (Part III) and § 10-4 (Part II); the BFF enforces the same minimum.
export const MIN_CANCELLATION_REASON_LENGTH = 30;

export const cancellationNeedsCodedReason = (event: SourcingEventDto, now: number): boolean => {
  if (event.eventContext.type !== "Public" || !event.canCancel) return false;
  if (event.status !== SourcingEventStatus.Published) return true;
  const deadline = event.announcedBidSubmissionDeadline ?? event.milestoneDates.bidSubmissionDeadline;
  return deadline != null && now >= Date.parse(deadline);
};

const isCancellationError = (e: unknown, errorCode: string): boolean =>
  isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode === errorCode;

export const useCancelOrCloseProcurement = (event: SourcingEventDto) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const [cancelEvent, { isLoading: isCancelling }] = useCancelSourcingEventMutation();
  const [closeEvent, { isLoading: isClosing }] = useCloseSourcingEventMutation();
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const [reason, setReason] = useState("");
  const [nonAwardReason, setNonAwardReason] = useState<NonAwardReason | "">("");
  const [relaunch, setRelaunch] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [serverSaysDeadline, setServerSaysDeadline] = useState<"open" | "passed" | null>(null);

  const onDoffin = announcedOnDoffin(event);
  const codedWhileCancellable = useRef(false);
  if (reasonAction === "cancel" && event.canCancel) {
    codedWhileCancellable.current = cancellationNeedsCodedReason(event, Date.now());
  }
  const mode: CancelMode =
    reasonAction === "cancel" && event.status === SourcingEventStatus.Awarded && event.eventContext.type !== "Public"
      ? "withdrawAward"
      : reasonAction !== "cancel" || event.eventContext.type !== "Public"
      ? "plain"
      : serverSaysDeadline === "passed" || (serverSaysDeadline !== "open" && codedWhileCancellable.current)
        ? "afterDeadline"
        : "beforeDeadline";
  const isPublicCancel = mode === "beforeDeadline" || mode === "afterDeadline";
  const {
    data: scope,
    isLoading: scopeLoading,
    isError: scopeFailed,
  } = useGetCancellationScopeQuery(isPublicCancel ? { eventId: event.id } : skipToken);
  const trimmedLength = visibleLength(reason);
  const longEnough = trimmedLength >= MIN_CANCELLATION_REASON_LENGTH;
  const needsSomeReason =
    reasonAction === "cancel" && mode === "plain" && event.eventContext.type === "MiniCompetition";
  const canSubmit =
    (mode === "plain" && (!needsSomeReason || trimmedLength > 0)) ||
    (mode === "beforeDeadline" && longEnough && confirmed) ||
    (mode === "afterDeadline" && longEnough && nonAwardReason !== "") ||
    (mode === "withdrawAward" && longEnough);

  const reset = () => {
    setReasonAction(null);
    setReason("");
    setNonAwardReason("");
    setRelaunch(false);
    setConfirmed(false);
    setServerSaysDeadline(null);
  };

  const submitReason = async () => {
    const value = reason.trim() === "" ? null : reason.trim();
    try {
      if (reasonAction === "cancel") {
        const cancelled = await cancelEvent({
          eventId: event.id,
          cancelSourcingEventRequest:
            mode === "afterDeadline"
              ? { reason: value, nonAwardReason: nonAwardReason || null, relaunch }
              : { reason: value },
        }).unwrap();
        const notified = cancelled.cancellation?.notifiedSuppliers;
        if (notified != null) {
          toast({
            title: t("The procurement is cancelled. {{count}} suppliers have been notified.", { count: notified }),
            status: "success",
            position: "top-right",
          });
        }
      } else if (reasonAction === "close") {
        await closeEvent({
          eventId: event.id,
          closeSourcingEventRequest: { reason: value },
        }).unwrap();
      }
      reset();
    } catch (e) {
      if (isCancellationError(e, "zz_SourcingEventApiErrorCode_EventAlreadyCancelled")) {
        reset();
        trigger(e);
      } else if (
        isCancellationError(e, "zz_FrameworkAgreementApiErrorCodeAwardedMiniCompetitionCanOnlyBeCancelled") ||
        isCancellationError(e, "zz_SourcingEventApiErrorCode_PublicProcurementEndsByCancelling") ||
        isCancellationError(e, "zz_SourcingEventApiErrorCode_CannotCancelConcludedContract") ||
        isCancellationError(e, "zz_SourcingEventApiErrorCode_MiniCompetitionEndsByCancelling")
      ) {
        reset();
        trigger(e);
      } else if (isCancellationError(e, "zz_SourcingEventApiErrorCode_CancellationReasonCodeNotYetDue")) {
        setServerSaysDeadline("open");
      } else if (isCancellationError(e, "zz_SourcingEventApiErrorCode_CancellationReasonCodeRequired")) {
        setServerSaysDeadline("passed");
      } else {
        trigger(e);
      }
    }
  };

  // t('{{count}} tenders received', { count }) t('{{count}} suppliers registered interest', { count })
  const count = (value: number | undefined, key: string) =>
    scopeLoading ? (
      <Skeleton h="4" w="40" aria-label={t("Counting") ?? undefined} />
    ) : scopeFailed || value == null ? (
      <Text as="span" color="smError">
        {t("Could not fetch the number")}
      </Text>
    ) : (
      t(key, { count: value })
    );

  const modal = (
    <Modal isOpen={reasonAction != null} onClose={reset}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {reasonAction === "cancel" ? t("Cancel the procurement?") : t("Close the procurement?")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="5">
            {serverSaysDeadline === "open" && (
              <Alert status="info" rounded="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t(
                    "The bid deadline has not passed yet, so the cancellation needs no cause. Check the reason and cancel again.",
                  )}
                </AlertDescription>
              </Alert>
            )}
            {serverSaysDeadline === "passed" && (
              <Alert status="info" rounded="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t(
                    "The bid deadline passed while the dialog was open. A cancellation after the deadline is announced with a result notice, so choose the cause and cancel again.",
                  )}
                </AlertDescription>
              </Alert>
            )}
            {mode === "beforeDeadline" && (
              <Alert status="warning" rounded="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t("Cancelling is final. Any tenders delivered will not be continued.")}
                </AlertDescription>
              </Alert>
            )}
            {mode === "afterDeadline" && event.status === SourcingEventStatus.Awarded && (
              <Alert status="warning" rounded="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t(
                    "The award is withdrawn. Every supplier, the winner included, is told that the procurement is cancelled and why.",
                  )}
                </AlertDescription>
              </Alert>
            )}
            {mode === "withdrawAward" && (
              <Alert status="warning" rounded="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t(
                    "The framework agreement was terminated, so no call-off can be made from this award. Every participant, the winner included, is told that the mini-competition is cancelled and why.",
                  )}
                </AlertDescription>
              </Alert>
            )}
            {isPublicCancel && (
              <List spacing="1" aria-label={t("Affected by the cancellation") ?? undefined}>
                <ListItem>{count(scope?.tendersReceived, "{{count}} tenders received")}</ListItem>
                <ListItem>
                  {count(scope?.suppliersRegisteredInterest, "{{count}} suppliers registered interest")}
                </ListItem>
              </List>
            )}
            {mode === "afterDeadline" && (
              <FormControl isRequired>
                <FormLabel htmlFor="cancellation-reason-code">{t("Cause of the cancellation")}</FormLabel>
                <Select
                  id="cancellation-reason-code"
                  placeholder={t("Choose the cause") ?? undefined}
                  value={nonAwardReason}
                  onChange={(e) => setNonAwardReason(e.target.value as NonAwardReason)}
                >
                  {nonAwardReasons(t).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl
              isRequired={isPublicCancel || mode === "withdrawAward" || needsSomeReason}
              isInvalid={mode !== "plain" && trimmedLength > 0 && !longEnough}
            >
              <FormLabel htmlFor="cancellation-reason">{t("Reason")}</FormLabel>
              {mode === "beforeDeadline" && (
                <Text fontSize="sm" color="smMuted" pb="2">
                  {onDoffin
                    ? t("The text is visible to the suppliers and published on Doffin. Avoid confidential details.")
                    : t("The text is visible to the suppliers. Avoid confidential details.")}
                </Text>
              )}
              <Textarea
                id="cancellation-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  (mode === "beforeDeadline"
                    ? t(
                        "For example: No tenders received, Only invalid or unacceptable tenders, Significant change in need, Budget cut, Process error",
                      )
                    : t("Shared with the invited suppliers")) ?? undefined
                }
              />
              {mode !== "plain" && (
                <>
                  <FormHelperText>
                    {t("{{count}} / {{min}} minimum characters", {
                      count: trimmedLength,
                      min: MIN_CANCELLATION_REASON_LENGTH,
                    })}
                  </FormHelperText>
                  <FormErrorMessage>
                    {t("The reason must be at least 30 characters and explain the proper grounds")}
                  </FormErrorMessage>
                </>
              )}
            </FormControl>
            {mode === "beforeDeadline" && (
              <Checkbox isChecked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}>
                {t("I confirm that the procurement is cancelled")}
              </Checkbox>
            )}
            {mode === "afterDeadline" && (
              <Checkbox isChecked={relaunch} onChange={(e) => setRelaunch(e.target.checked)}>
                {t("The competition will be announced again")}
              </Checkbox>
            )}
            {mode === "afterDeadline" && onDoffin && (
              <Text fontSize="sm" color="smMuted">
                {t("After the cancellation, announce the result on Doffin from the procurement's overview.")}
              </Text>
            )}
            {mode === "beforeDeadline" && onDoffin && (
              <Text fontSize="sm" color="smMuted">
                {t("After the cancellation, send the change notice to Doffin from the procurement's overview.")}
              </Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={reset}>
            {t("Abort")}
          </Button>
          <Button
            colorScheme="red"
            onClick={submitReason}
            isDisabled={!canSubmit}
            isLoading={isCancelling || isClosing}
          >
            {reasonAction === "cancel" ? t("Cancel the procurement") : t("Close the procurement")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return { open: setReasonAction, modal };
};
